import { NO_PRIVACY_CEILING, PRIVACY_CEILINGS, VaultRequestError, decodeGrantDoc } from '@cef-ai/vault-sdk';
import type {
  Member,
  MembershipSummary,
  PrivacyCeilingName,
  Scope,
  ScopeGrants,
  ScopeLevel,
  MemberRole,
  SignedGrant,
  SignedRoster,
  Vault,
} from '@cef-ai/vault-sdk';
import { canonicalMemberPubkey } from '@/lib/clients/vault/org-members';

/**
 * The ORGANIZATIONS read client — the switcher's list and the read-only
 * scope/roster view of a selected org. Bearer-only, sibling of
 * `read-client.ts`: every method here runs on the DelegationAuthProvider
 * instance (or a plain test double), so it is safe from a boot effect or a
 * timer and NEVER signs. The write side (member add/edit, grant and ceiling
 * writes) is `org-members.ts` on the signing SDK; the wizard's rename signs
 * in its own run click.
 */

/**
 * The SDK surface these reads need, narrowed so unit tests can pass a plain
 * object and the real Bearer instance (a full `VaultSDK`) satisfies it
 * structurally. `memberships.list` is readable by the owner or any LIVE
 * member; `vault.byId` by anyone the vault discloses itself to.
 */
export interface OrgsReadSdk {
  vault: {
    current: () => Promise<Vault>;
    byId: (vaultId: string) => Promise<Vault>;
  };
  memberships: {
    mine: () => Promise<MembershipSummary[]>;
    list: (vaultId: string) => Promise<Member[]>;
  };
}

/** One scope grant, flattened from the `ScopeGrants` record for ordered UI. */
export interface OrgScopeGrantView {
  name: string;
  level: ScopeLevel;
}

/** One switcher entry: the caller's own vault, or a vault they were invited into. */
export interface OrgSummary {
  vaultId: string;
  /** `displayName ?? vaultId` — a vault that was never named has no label. */
  name: string;
  role: MemberRole;
  /** True only on the synthesized owner entry (the caller's own vault). */
  isOwner: boolean;
  /** The caller's own grants on that vault; `[]` for the owner (see below). */
  scopes: OrgScopeGrantView[];
}

/** Map a grants record to ordered entries (insertion order of the record). */
export function orgScopeGrants(grants: ScopeGrants): OrgScopeGrantView[] {
  return Object.entries(grants).map(([name, level]) => ({ name, level }));
}

/**
 * The switcher's list: `memberships.mine()` (the vaults this wallet was
 * invited into) joined with `vault.current()` (the vault it owns), owner
 * entry first.
 *
 * `mine()` EXCLUDES the caller's own vault on purpose (the owner is never a
 * membership row — see the SDK's `Memberships` docs), so the join is the only
 * way to show both without double-counting. The own-vault read may 404 (a
 * connected wallet that never claimed one): that drops the owner entry, not
 * the whole list — every other failure propagates.
 */
export async function fetchOrgs(sdk: OrgsReadSdk): Promise<OrgSummary[]> {
  const [own, joined] = await Promise.all([
    sdk.vault.current().catch((e: unknown) => {
      if (e instanceof VaultRequestError && e.status === 404) return null;
      throw e;
    }),
    sdk.memberships.mine(),
  ]);
  const entries: OrgSummary[] = [];
  if (own) {
    entries.push({
      vaultId: own.id,
      name: own.displayName ?? own.id,
      // Mirrors the roster's synthesized owner row (`isOwner: true, role:
      // "admin"`): owning is expressed as the admin role server-side.
      role: 'admin',
      isOwner: true,
      // The owner holds everything by key math — there are no grant rows to
      // list, and inventing them would misstate the disclosure model.
      scopes: [],
    });
  }
  for (const m of joined) {
    entries.push({
      vaultId: m.vaultId,
      name: m.displayName ?? m.vaultId,
      role: m.role,
      isOwner: false,
      scopes: orgScopeGrants(m.scopes),
    });
  }
  return entries;
}

/** The read-only view of one org: its scopes and its member roster. */
export interface OrgDetail {
  vaultId: string;
  /** The org vault's scopes (full SDK rows — `name` + `displayName` at least). */
  scopes: Scope[];
  /**
   * The roster in server order (synthesized owner row first). `Member.scopes`
   * keeps its three states — grant set / `{}` / absent (= not disclosed to
   * this caller) — so the renderer can be disclosure-rule-honest.
   */
  members: Member[];
}

/**
 * Both org-vault reads in parallel — they are independent, and the section
 * wants to render them together or not at all. Both are Bearer GETs.
 */
export async function fetchOrgDetail(sdk: OrgsReadSdk, vaultId: string): Promise<OrgDetail> {
  const [vault, members] = await Promise.all([
    sdk.vault.byId(vaultId),
    sdk.memberships.list(vaultId),
  ]);
  return { vaultId, scopes: await vault.scopes.list(), members };
}

/**
 * The SDK surface the signed-roster read needs, narrowed like `OrgsReadSdk`
 * so its test doubles stay plain objects and the Bearer `VaultSDK` satisfies
 * it structurally. Kept a SEPARATE interface rather than growing
 * `OrgsReadSdk`: the ceiling read is its own fetch lifecycle (its own hook,
 * its own reload), so a double serving the detail reads must not have to
 * grow a roster mock to keep compiling — the same one-surface-one-narrowing
 * rule `OrgMembersWriteSdk` follows on the write side.
 */
export interface SignedRosterReadSdk {
  memberships: {
    signedRoster: (vaultId: string) => Promise<SignedRoster>;
  };
}

/**
 * A member's privacy ceiling as the roster renders it: one of the four
 * ceilings, `'none'` (a v1 document — the owner never signed one; NOT
 * "public", which would invent a disclosure), or `'unknown'` (no readable
 * document: not served by the signed-roster route, or undecodable).
 */
export type MemberCeiling = PrivacyCeilingName | 'none' | 'unknown';

/**
 * Is `c` one of the four ceilings a grant may carry? The writer-side guard
 * for values already flowing through the UI: `'none'`/`'unknown'` are
 * display states of the read model, never choices a write may send.
 */
export function isPrivacyCeilingName(c: string): c is PrivacyCeilingName {
  return (PRIVACY_CEILINGS as readonly string[]).includes(c);
}

/**
 * Standard-alphabet base64 → bytes: the roster's `doc` field is a Go
 * `[]byte` as `encoding/json` renders it, and `decodeGrantDoc` eats bytes.
 */
function docBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/**
 * One signed grant's ceiling, or why it cannot be read. `'none'` is claimed
 * ONLY on the explicit `NO_PRIVACY_CEILING` sentinel (v1 document); a
 * decoded name this build does not know maps to `'unknown'`, not `'none'` —
 * today's decoder refuses such a document, but a future 4.x resolved under
 * ^4.2.0 may widen its decoder and ceiling set together, and an unknown name
 * is then a real signed value this build cannot render, never an attestation
 * that nothing was signed. A decode that FAILS — a future schema, a corrupt
 * row — degrades the same way rather than throwing: one unreadable entry
 * must not cost every other member their display value.
 *
 * Exported since Task 6: the memory bank's access explainer decodes the
 * VIEWER's own ceiling out of the same signed documents, and a second copy
 * of this decode (with its none-vs-unknown doctrine) would drift.
 */
export function ceilingOfGrant(grant: SignedGrant): MemberCeiling {
  try {
    const { privacyCeiling } = decodeGrantDoc(docBytes(grant.doc));
    if (privacyCeiling === NO_PRIVACY_CEILING) return 'none';
    return isPrivacyCeilingName(privacyCeiling) ? privacyCeiling : 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Each member's signed privacy ceiling, keyed by canonical pubkey, read from
 * the SIGNED ROSTER because the roster row does NOT carry the ceiling on the
 * wire — it exists only inside the owner-signed grant document, as the field
 * that makes that document v2. Bearer-only like every read here.
 *
 * Members the route does not serve (suspended, expired — it serves LIVE
 * memberships only) are simply ABSENT from the map; the caller's lookup
 * default stands in for them, because absence is not `'none'`: "none
 * signed" is the owner's attestation that no disclosure was ever signed,
 * which a filtered-out document says nothing about.
 */
export async function fetchMemberCeilings(
  sdk: SignedRosterReadSdk,
  vaultId: string,
): Promise<Record<string, MemberCeiling>> {
  const roster = await sdk.memberships.signedRoster(vaultId);
  const ceilings: Record<string, MemberCeiling> = {};
  for (const grant of roster.grants) {
    // Keyed CANONICALLY (raw spelling only when the key is not hex-shaped
    // at all): the map is written from signed-roster memberPubkeys and read
    // by roster-row memberPubkeys — two routes that could drift in spelling,
    // and a drifted key silently reads its row as Unknown.
    ceilings[canonicalMemberPubkey(grant.memberPubkey) ?? grant.memberPubkey] = ceilingOfGrant(grant);
  }
  return ceilings;
}

/**
 * The member's signed ceiling out of the map — the lookup half of the
 * canonical-key discipline {@link fetchMemberCeilings} writes, so a spelling
 * drift between the two routes cannot cost a row its value. A miss reads as
 * `'unknown'`, never `'none'`: absence from the map is absence of a READ,
 * not the owner's attestation that nothing was signed.
 */
export function ceilingForMember(
  ceilings: Record<string, MemberCeiling>,
  memberPubkey: string,
): MemberCeiling {
  return ceilings[canonicalMemberPubkey(memberPubkey) ?? memberPubkey] ?? 'unknown';
}
